/* eslint-disable @typescript-eslint/no-unused-vars */
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import cx from 'classnames/bind';
import { useMediaQuery } from 'react-responsive';
import { motion, AnimatePresence } from 'framer-motion';

import { Layout } from '@/components/Layout';
import { Message } from '@/components/Message';
import { Button } from '@/components/Button';
import { Notification } from '@/components/Notification';
import { Hint } from '@/components/Hint';
import HintError from '@/components/HintError/HintError';
import { Header } from '../../Header';
import SatisfiedManager from '@/assets/satisfiedManager.png';
import SatisfiedBuyer1 from '@/assets/satisfiedBuyer1.png';
import SatisfiedBuyer2 from '@/assets/satisfiedBuyer2.png';
import SatisfiedBuyer3 from '@/assets/satisfiedBuyer3.png';
import SatisfiedBuyer4 from '@/assets/satisfiedBuyer4.png';
import DissatisfiedManager from '@/assets/dissatisfiedManager.png';
import DissatisfiedBuyer1Right from '@/assets/dissatisfiedBuyer1Right.png';
import DissatisfiedBuyer2Right from '@/assets/dissatisfiedBuyer2Right.png';
import DissatisfiedBuyer3Right from '@/assets/dissatisfiedBuyer3Right.png';
import DissatisfiedBuyer4Right from '@/assets/dissatisfiedBuyer4Right.png';
import Cashbox from '@/assets/cashbox.png';
import CashboxTablet from '@/assets/cashboxTablet.png';
import CashboxDesktop from '@/assets/cashboxDesktop.png';
import type { IQuestion } from '@/types/question';
import type { IGame } from '@/types/game';
import type { IProduct } from '@/types/product';

import styles from './Step4.module.scss';

interface Step4Props {
  setStep: (value: number) => void;
  setIsScore: (value: boolean) => void;
  setNumberQuestion: Dispatch<SetStateAction<number>>;
  selectedAnswer: string[];
  answers: any;
  numberQuestion: number;
  questions: IQuestion[];
  idGame: number;
  scenarios: IGame[];
  isRightAnswer: boolean;
  responseStatus: { [x: number]: boolean | null };
  setScenarios: Dispatch<React.SetStateAction<IGame[]>>;
  configId: string;
  userToken: string;
  product: IProduct;
  costPerQuestion: number;
  receivedPoints: number;
  setResponseStatus: Dispatch<SetStateAction<{ [x: number]: boolean | null }>>;
  game: IGame;
  totalPoints: number;
}

const cn = cx.bind(styles);

const simpleHash = (configId: string) =>
  configId
    ?.split('')
    ?.reduce(
      // @ts-ignore
      // eslint-disable-next-line no-bitwise
      (acc, char) => (acc << 5) - acc + char.charCodeAt(0),
      '90367125123',
    )
    // @ts-ignore
    ?.toString(16);

const createLog = (
  token: string,
  available_score: number,
  awarded_score: number,
  config_id: string,
) => {
  fetch(`https://admin.informer.jti-games.dev.familyagency.ru/api/logs/score`, {
    method: 'POST',
    headers: {
      UserToken: token,
      accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      token,
      config_id,
      awarded_score,
      available_score,
    }),
  });



  // TEST LOG: вывод данных, которые отправляются в бекенд игры
  console.log('TEST LOG: вывод данных, которые отправляются в бекенд игры запросом api/logs/score --- ', {
    token,
    config_id,
    awarded_score,
    available_score,
  })

};

const Step4: FC<Step4Props> = ({
  setStep,
  configId,
  setIsScore,
  setNumberQuestion,
  selectedAnswer,
  answers,
  numberQuestion,
  questions,
  idGame,
  scenarios,
  isRightAnswer,
  responseStatus,
  setScenarios,
  userToken,
  product,
  costPerQuestion,
  receivedPoints,
  setResponseStatus,
  game,
  totalPoints,
}) => {
  const [isShowAnswer, setIsShowAnswer] = useState(true);

  const isTablet = useMediaQuery({ query: '(min-width: 834px)' });
  const isDesctop = useMediaQuery({ query: '(min-width: 1440px)' });

  const currentScenarioIndex = scenarios.findIndex(({ id }) => id === idGame);
  const isScenarioWithPoints = scenarios[currentScenarioIndex]?.isScore;

  const setScore = async () => {
    try {
      setIsScore(false);

      const currentScenario = scenarios.find(({ id }) => id === idGame);
      if (!currentScenario) return;

      const { status: isCompletedScenario, achievement: isCompletedSuccessScenario } = currentScenario;

      if (isCompletedSuccessScenario) return;

      await fetch(
        `https://admin.informer.jti-games.dev.familyagency.ru/api/player/games/${idGame}`,
        {
          method: 'POST',
          headers: {
            UserToken: userToken,
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(
            isCompletedScenario
              ? { achievement: isRightAnswer }
              : { score: receivedPoints, achievement: isRightAnswer }
          ),
        }
      );

      const gamesResponse = await fetch(
        `https://admin.informer.jti-games.dev.familyagency.ru/api/segments/${process.env.REACT_APP_SEGMENT}/games`,
        {
          headers: { UserToken: userToken },
        }
      );

      const { data } = await gamesResponse.json();
      const updatedGames = data.games;

      setScenarios(updatedGames);

      const lastScenarioId = updatedGames[updatedGames.length - 1]?.id;

      const points = updatedGames.reduce(
        (acc: number, current: { score: number }) => acc + (current.score || 0),
        0
      );

      if (
        !isCompletedScenario &&
        isScenarioWithPoints &&
        idGame === lastScenarioId
      ) {
        setIsScore(true);

        const id = simpleHash(configId);
        createLog(userToken, totalPoints, points, id);

        const scoreValue = points / totalPoints;

        console.log("SDK payload:", {
          points: scoreValue,
          meta: { meta1: 100, id },
          config: { addToRanking: true },
        });

        try {
          const res = await window.jticonnexus.setScore(
            scoreValue,
            { meta1: 100 },
            { addToRanking: true }
          );
          console.log("SDK success:", res);
        } catch (err) {
          console.log("SDK error:", err);
        }
      }
    } catch (error) {
      console.error("setScore error:", error);
    }
  };

  // const setScore = () => {
  //   setIsScore(false);
  //   const isCompletedScenario = scenarios.find(
  //     ({ id }) => id === idGame,
  //   )?.status;
  //   const isCompletedSuccessScenario = scenarios.find(
  //     ({ id }) => id === idGame,
  //   )?.achievement;
  //
  //   if (!isCompletedSuccessScenario) {
  //     fetch(
  //       `https://admin.informer.jti-games.dev.familyagency.ru/api/player/games/${idGame}`,
  //       {
  //         method: 'POST',
  //         headers: {
  //           UserToken: userToken,
  //           accept: 'application/json',
  //           'Content-Type': 'application/json',
  //         },
  //         body: isCompletedScenario
  //           ? JSON.stringify({
  //               achievement: isRightAnswer,
  //             })
  //           : JSON.stringify({
  //               score: receivedPoints,
  //               achievement: isRightAnswer,
  //             }),
  //       },
  //     )
  //       .then((response) => response.json())
  //       .then(() => {
  //         fetch(
  //           `https://admin.informer.jti-games.dev.familyagency.ru/api/segments/${process.env.REACT_APP_SEGMENT}/games`,
  //           {
  //             headers: {
  //               UserToken: userToken,
  //             },
  //           },
  //         )
  //           .then((response) => response.json())
  //           .then(({ data }) => {
  //             setScenarios(data.games);
  //             const lastScenarioId = scenarios[scenarios.length - 1].id;
  //
  //             const points = data.games.reduce(
  //               (acc: number, current: { score: number }) =>
  //                 acc + current.score,
  //               0,
  //             );
  //
  //             if (
  //               !isCompletedScenario &&
  //               isScenarioWithPoints &&
  //               idGame === lastScenarioId
  //             ) {
  //               setIsScore(true);
  //               const id = simpleHash(configId);
  //               createLog(userToken, totalPoints, points, id);
  //
  //               window.jticonnexus.setScore(
  //                 // { meta1: 100, id: null, },
  //                 points / totalPoints,
  //                 { meta1: 100 },
  //                 { addToRanking: true },
  //               ).then(res => {
  //                 // TEST LOG: результат выполнения метода setScore SDK jticonnexus при успешном выполнеии
  //                 console.log('TEST LOG: результат выполнения метода setScore SDK jticonnexus при успешном выполнеии --- ', res)
  //
  //               }).catch(err => {
  //                 // TEST LOG: результат выполнения метода setScore SDK jticonnexus при ошибке
  //                 console.log('TEST LOG: результат выполнения метода setScore SDK jticonnexus при успешном выполнеии --- ', err)
  //               });
  //
  //               // TEST LOG: вывод данных score, которые отправляются в SDK jticonnexus методом setScore
  //               console.log('TEST LOG: вывод данных score, которые отправляются в SDK jticonnexus методом setScore --- ', {
  //                 points: points / totalPoints,
  //                 meta:  { meta1: 100, id },
  //                 config: { addToRanking: true },
  //               })
  //             }
  //           });
  //       });
  //   }
  // };

  const endGame = () => {
    const obj = { ...game.questions.map(() => null) };
    setResponseStatus(obj);

    fetch(
      'https://admin.informer.jti-games.dev.familyagency.ru/api/custom-logs/store',
      {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
          'token': userToken,
          'game_id': idGame,
          'question_id' : numberQuestion,
          'action' : 'завершение сценария',
          'result' : isRightAnswer.toString(),
          'available_score' : totalPoints,
          'awarded_score' : receivedPoints,
        }),
      },
    )

    setStep(13);
  };

  const continueGame = () => {
    setStep(10);
    setNumberQuestion((prev: number) => prev + 1);

    fetch(
      'https://admin.informer.jti-games.dev.familyagency.ru/api/custom-logs/store',
      {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
          'token': userToken,
          'game_id': idGame,
          'question_id' : numberQuestion,
          'action' : 'продолжение сценария',
          'result' : isRightAnswer.toString(),
          'available_score' : totalPoints,
          'awarded_score' : receivedPoints,
        }),
      },
    )
  };

  const currentScenario = scenarios.find(({ id }) => id === idGame);
  const isCompletedGame = currentScenario?.status;

  const MotionNotification = motion(Notification);
  const MotionMessage = motion(Message);
  const MotionHint = motion(Hint);
  const MotionHintError = motion(HintError);

  useEffect(() => {
    if (
      questions.length !== numberQuestion &&
      (isCompletedGame || !isRightAnswer || !isScenarioWithPoints)
    ) {
      setIsShowAnswer(false);
    }
  }, [questions, numberQuestion, isRightAnswer, isCompletedGame]);

  const srcCashbox = isDesctop
    ? CashboxDesktop
    : isTablet
      ? CashboxTablet
      : Cashbox;

  const srcManager = isRightAnswer ? SatisfiedManager : DissatisfiedManager;

  const srcBuyer1 = isRightAnswer ? SatisfiedBuyer1 : DissatisfiedBuyer1Right;

  const srcBuyer2 = isRightAnswer ? SatisfiedBuyer2 : DissatisfiedBuyer2Right;

  const srcBuyer3 = isRightAnswer ? SatisfiedBuyer3 : DissatisfiedBuyer3Right;

  const srcBuyer4 = isRightAnswer ? SatisfiedBuyer4 : DissatisfiedBuyer4Right;

  const src = !scenarios[currentScenarioIndex]?.isScore
    ? srcManager
    : currentScenarioIndex === 0 ||
        (currentScenarioIndex + (!scenarios[0].isScore ? 3 : 0)) % 4 === 0
      ? srcBuyer1
      : (currentScenarioIndex + (!scenarios[0].isScore ? 2 : 3)) % 4 === 0
        ? srcBuyer2
        : (currentScenarioIndex + (!scenarios[0].isScore ? 1 : 2)) % 4 === 0
          ? srcBuyer3
          : srcBuyer4;

  const currentScenarioId = scenarios.find(({ id }) => id === idGame)?.id;

  const isLastScenario =
    currentScenarioId === scenarios[scenarios.length - 1].id;

  const isPenultimate =
    currentScenarioId === scenarios[scenarios.length - 2].id;

  useEffect(() => {
    if (isShowAnswer && isPenultimate && isRightAnswer) {
      setIsShowAnswer(false);
    }
  }, [isShowAnswer, isPenultimate, isRightAnswer]);

  useEffect(() => {
    if (questions.length === numberQuestion) {
      setScore();
    }
  }, [questions.length, numberQuestion]);

  return (
    <>
      <div className={cn('background')} />
      <div
        className={cn(
          'imgWrapper',
          `${questions.length === numberQuestion ? 'imgWrapper-rotate' : ''}`,
        )}
      >
        <img src={src} className={cn('img')} alt="Консультант" />
      </div>
      <img src={srcCashbox} className={cn('imgCashbox')} alt="Касса" />
      <Layout className={cn('container')}>
        <Header
          numberQuestion={numberQuestion}
          questions={questions}
          scenarios={scenarios}
          points={receivedPoints}
          responseStatus={responseStatus}
        />
        {isShowAnswer &&
          isRightAnswer &&
          !isCompletedGame &&
          isScenarioWithPoints && (
            <AnimatePresence>
              <MotionNotification
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }}
                transition={{ duration: 1.7, delay: 0.1, ease: 'backInOut' }}
                onAnimationComplete={() => {
                  setIsShowAnswer(false);
                }}
                className={cn('points')}
                points={costPerQuestion}
              />
            </AnimatePresence>
          )}
        {isShowAnswer &&
          isScenarioWithPoints &&
          questions.length === numberQuestion &&
          !isLastScenario &&
          !isPenultimate && (
            <AnimatePresence>
              <MotionMessage
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }}
                transition={{ duration: 2, ease: 'backInOut' }}
                onAnimationComplete={() => {
                  setIsShowAnswer(false);
                }}
                className={cn('message')}
              >
                {isRightAnswer
                  ? `Спасибо, я возьму сигареты ${product.title}`
                  : 'Это не то, что мне нужно. Я ухожу'}
              </MotionMessage>
            </AnimatePresence>
          )}
        {isShowAnswer && isPenultimate && !isRightAnswer && (
          <AnimatePresence>
            <MotionMessage
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 2, ease: 'backInOut' }}
              onAnimationComplete={() => {
                setIsShowAnswer(false);
              }}
              className={cn('message')}
            >
              Это не то, что мне нужно. Я ухожу
            </MotionMessage>
          </AnimatePresence>
        )}
        {(!isShowAnswer ||
          isLastScenario ||
          (!isScenarioWithPoints && questions.length === numberQuestion)) && (
          <>
            {isRightAnswer ? (
              <MotionHint
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }}
                transition={{ duration: 1.5, ease: 'backInOut', delay: 1.1 }}
                className={cn('hint')}
              >
                Покупатель высоко оценил Вашу готовность ему помочь и прекрасное
                знание ассортимента
              </MotionHint>
            ) : (
              <MotionHintError
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }}
                transition={{ duration: 2, ease: 'backInOut' }}
                className={cn('hint-error')}
                selectedAnswer={selectedAnswer}
                answers={answers}
              />
            )}
            {questions.length === numberQuestion ? (
              <Button className={cn('button')} handleClick={endGame}>
                Завершить сценарий
              </Button>
            ) : (
              <Button className={cn('button')} handleClick={continueGame}>
                Продолжить
              </Button>
            )}
          </>
        )}
      </Layout>
    </>
  );
};

export default Step4;
